import { MenuItem, useToast } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCancelFundingRequestMutation,
  useGetFundingRequestWithBaseDetailsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { PermissionGuard, usePermissionGuard } from '~/core/PermissionGuard';
import { Icon } from '~/packages/common-frontend/components/icons';

/**
 * Hook to assess if a funding request can be canceled.
 */
export const useCanCancelFundingRequest = (params: {
  fundingRequestId: string;
}) => {
  const { fundingRequestId } = params;

  const isCancelApprovedEnabled =
    usePermissionGuard(
      ({ flags }) => flags.productsFundingRequestCancelApprovedPermissions,
    ) === 'allow';

  const fundingRequestQuery = useGetFundingRequestWithBaseDetailsQuery({
    skip: !fundingRequestId,
    variables: { fundingRequestId },
  });

  const canCancel = useMemo<boolean>(() => {
    if (!fundingRequestQuery.data) return false;

    const fundingRequest =
      fundingRequestQuery.data.fundingRequestWithFullDetails;

    // If funding request is in a "pending" or "submitted" state,
    // then these are valid options to cancel the request.
    if (
      fundingRequest.status === 'pending' ||
      fundingRequest.status === 'submitted'
    ) {
      return true;
    }

    // If funding request is in an "approved" state and is enabled,
    // then we can cancel the request if it hasn't settled yet
    if (isCancelApprovedEnabled && fundingRequest.status === 'approved') {
      const hasSettled = fundingRequest.fundingRequestComponents.some(
        (frc) => frc.amount > 0 && !!frc.postingDate,
      );
      if (!hasSettled) return true;
    }

    return false;
  }, [fundingRequestQuery.data, isCancelApprovedEnabled]);

  return {
    canCancel,
    isLoading: fundingRequestQuery.loading || !fundingRequestQuery.data,
  };
};

interface CancelFundingRequestProps {
  fundingRequestId: string;
}
export const CancelFundingRequestMenuItem = ({
  fundingRequestId,
}: CancelFundingRequestProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { canCancel, isLoading } = useCanCancelFundingRequest({
    fundingRequestId,
  });

  const [
    cancelFundingRequestMutation,
    { loading: isCancelLoading },
  ] = useCancelFundingRequestMutation();

  if (!fundingRequestId || !canCancel) {
    return null;
  }

  return (
    <PermissionGuard
      permissions={({ flags }) =>
        flags.productsFundingRequestCancelApprovedPermissions
      }
    >
      <MenuItem
        icon={<Icon icon="x" />}
        gap="1rem"
        isDisabled={isLoading || isCancelLoading || !canCancel}
        onClick={async () => {
          try {
            await cancelFundingRequestMutation({
              variables: { fundingRequestId },
            });

            toast({
              title: 'Funding request canceled',
              status: 'success',
              duration: 3000,
              position: 'top',
              isClosable: true,
            });

            navigate('/funding-requests');
          } catch (error) {
            const errorMessage = getErrorMessageFromApolloError({
              error,
              defaultMessage:
                'Error canceling funding request. Please try again or contact Finley support',
            });
            toast({
              title: errorMessage,
              status: 'error',
              duration: 6000,
              position: 'top',
              isClosable: true,
            });
          }
        }}
      >
        Cancel Request
      </MenuItem>
    </PermissionGuard>
  );
};
